/**
 * Kreditkarten und DATEV-Stammdaten eines Mandanten, in der Akte der
 * Kreditkartenseite. Gegenstueck zu den Bankkonten, ohne IBAN.
 *
 * Die Stammdaten sind der Kopf des Buchungsstapels (Beraternummer, Beginn
 * des Wirtschaftsjahres, Sachkontenlaenge). Je Karte stehen Kartenkonto und
 * Gegenkonto, das sind Feld 7 und 8 jeder Buchungszeile. Eine hinterlegte
 * Karte ordnet kuenftige Abrechnungen mit genau dieser Kennung selbst zu.
 *
 * Kopf und Konten erben von der DATEV-Vorgabe der Kanzlei (007). Beim
 * Mandanten steht dann nur noch die Mandantennummer; was er oder eine Karte
 * selbst eintraegt, geht vor.
 */
import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type DatevVorgabe, type Kreditkarte, type MandantKarten } from '../lib/api'
import { endeKurz, MONATE, NUR_CAMT } from '../lib/karte'
import { iban } from '../lib/format'
import { Fenster } from './Fenster'

const nurZiffern = (wert: string) => wert.replace(/[^0-9]/g, '')
const zahlOderNull = (wert: string) => (wert.trim() ? Number(wert.trim()) : null)

function fehlerText(problem: unknown, rueckfall: string): string {
  return problem instanceof ApiFehler ? problem.message : rueckfall
}

export function KartenStamm({ mandant, loeschbar, aufLoeschen }: {
  mandant: { id: string; name: string; mandantennummer: number | null }; loeschbar: boolean; aufLoeschen: () => void
}) {
  const mandantId = mandant.id
  const abfrage = useQuery({
    queryKey: ['mandantkarten', mandantId],
    queryFn: () => api.holen<MandantKarten>(`/api/mandanten/${mandantId}/karten`),
  })
  const [karteOffen, setzeKarteOffen] = useState<Kreditkarte | 'neu' | null>(null)
  const [entfernen, setzeEntfernen] = useState<Kreditkarte | null>(null)
  const d = abfrage.data

  if (abfrage.isPending) return <div className="leiser">Kreditkarten werden geladen …</div>
  if (abfrage.isError || !d) return <div className="fehler">{abfrage.error instanceof Error ? abfrage.error.message : 'Die Kreditkarten konnten nicht geladen werden.'}</div>

  return (
    <div className="abschnitt akte-formular">
      <StammFormular
        key={mandant.id}
        mandant={mandant}
        daten={d}
      />

      <div className="abschnitt-kopf abstand-oben">
        <h2>Kreditkarten</h2>
        <button type="button" className="knopf klein" onClick={() => setzeKarteOffen('neu')}>Karte hinzufügen</button>
      </div>
      <div className="kontenliste karten-stamm-liste">
        {d.karten.length === 0 && <p className="leiser">Noch keine Karte. Sie entsteht beim Zuordnen einer Abrechnung mit „beim Mandanten merken“ oder hier.</p>}
        {d.karten.map((k) => (
          <div key={k.id} className="kontozeile bearbeitbar" style={{ gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto' }}>
            <span className="einzeilig">
              <span>{k.anbieter} </span><span className="zahl">{endeKurz(k.endziffern)}</span>
              {k.bezeichnung && <span className="leiser"> · {k.bezeichnung}</span>}
            </span>
            {NUR_CAMT ? (
              <span className="leise zahl einzeilig" title="Ersatz-IBAN für DATEV, einmal als Bankverbindung zum Kartenkonto anlegen">{k.ersatz_iban ? iban(k.ersatz_iban) : ''}</span>
            ) : (() => {
              const konto = k.datev_konto ?? d.vorgabe?.kartenkonto ?? null
              const gegen = k.datev_gegenkonto ?? d.vorgabe?.gegenkonto ?? null
              const geerbt = k.datev_konto === null || k.datev_gegenkonto === null
              return konto !== null && gegen !== null
                ? <span className="leise zahl einzeilig">Konto {konto} an {gegen}{geerbt && <span className="leiser"> · Kanzlei-Vorgabe</span>}</span>
                : <span className="text-warnung einzeilig">Kartenkonto oder Gegenkonto fehlt</span>
            })()}
            <span className="zeilenaktionen">
              <button type="button" className="knopf still klein" onClick={() => setzeKarteOffen(k)}>Bearbeiten</button>
              <button type="button" className="knopf still klein" onClick={() => setzeEntfernen(k)}>Entfernen</button>
            </span>
          </div>
        ))}
      </div>

      <div className="formular-fuss">
        {loeschbar ? (
          <button type="button" className="knopf still links loeschknopf" onClick={aufLoeschen}>Mandant löschen</button>
        ) : (
          <span className="links leiser">Löschen geht erst, wenn keine Abrechnung mehr an diesem Mandanten hängt.</span>
        )}
      </div>

      {karteOffen && (
        <KarteFormular key={karteOffen === 'neu' ? 'neu' : karteOffen.id} mandantId={mandantId} karte={karteOffen === 'neu' ? null : karteOffen} vorgabe={d.vorgabe} aufSchliessen={() => setzeKarteOffen(null)} />
      )}

      {entfernen && <KarteEntfernen karte={entfernen} aufSchliessen={() => setzeEntfernen(null)} />}
    </div>
  )
}

/* ─────────────────────────────────────── Stammdaten des Mandanten */

type Kopf = { beraternummer: number; wj_beginn_monat: number; sachkontenlaenge: number }

/**
 * Alles, was der Buchungsstapel vom Mandanten braucht, in einem Formular.
 * Beraternummer, Sachkontenlaenge, Wirtschaftsjahr und die beiden Konten
 * sind aus der Vorgabe der Kanzlei vorausgefuellt (007). Beim ersten Mal
 * wird die Eingabe zur Vorgabe. Weicht danach etwas ab, entscheidet der
 * Knopf: nur fuer diesen Mandanten (datev_stamm und die Konten seiner
 * Karten) oder als neue Vorgabe fuer alle. Die Mandantennummer gehoert
 * immer nur dem Mandanten.
 */
function StammFormular({ mandant, daten }: { mandant: { id: string; name: string; mandantennummer: number | null }; daten: MandantKarten }) {
  const abfragen = useQueryClient()
  const v = daten.vorgabe
  const kopfBasis: Kopf | null = daten.stamm ?? v
  const ersteKarte = daten.karten[0]
  const kontoBasis = ersteKarte?.datev_konto ?? v?.kartenkonto ?? null
  const gegenBasis = ersteKarte?.datev_gegenkonto ?? v?.gegenkonto ?? null
  const text = (n: number | null | undefined) => (n != null ? String(n) : '')

  const [name, setzeName] = useState(mandant.name)
  const [nummer, setzeNummer] = useState(text(mandant.mandantennummer))
  const [berater, setzeBerater] = useState(kopfBasis ? String(kopfBasis.beraternummer) : text(daten.vorschlagBeraternummer))
  const [monat, setzeMonat] = useState(kopfBasis ? kopfBasis.wj_beginn_monat : 1)
  const [laenge, setzeLaenge] = useState(kopfBasis ? String(kopfBasis.sachkontenlaenge) : '4')
  const [konto, setzeKonto] = useState(text(kontoBasis))
  const [gegen, setzeGegen] = useState(text(gegenBasis))
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [gespeichert, setzeGespeichert] = useState<string | null>(null)

  const kopf: Kopf | null = berater.trim() ? { beraternummer: Number(berater), wj_beginn_monat: monat, sachkontenlaenge: zahlOderNull(laenge) ?? 4 } : null
  const mandantGeaendert = name.trim() !== mandant.name || zahlOderNull(nummer) !== mandant.mandantennummer
  const kopfGeaendert = !kopfBasis
    ? kopf !== null
    : kopf === null || kopf.beraternummer !== kopfBasis.beraternummer || kopf.wj_beginn_monat !== kopfBasis.wj_beginn_monat || kopf.sachkontenlaenge !== kopfBasis.sachkontenlaenge
  const kontenGeaendert = zahlOderNull(konto) !== kontoBasis || zahlOderNull(gegen) !== gegenBasis
  const vorgabeGeaendert = kopfGeaendert || kontenGeaendert

  async function speichern(wie: 'mandant' | 'vorgabe' | 'nur-mandant') {
    setzeFehler(null)
    setzeLaeuft(true)
    const kartenkonto = zahlOderNull(konto)
    const gegenkonto = zahlOderNull(gegen)
    try {
      if (mandantGeaendert) {
        await api.aendern(`/api/mandanten/${mandant.id}`, { name: name.trim(), mandantennummer: zahlOderNull(nummer) })
      }
      if (wie === 'vorgabe' && kopf) {
        await api.ersetzen('/api/kanzlei/datev-vorgabe', { ...kopf, kartenkonto, gegenkonto })
        // Dieser Mandant folgt ab jetzt der Vorgabe: eigene Konten seiner Karten fallen weg.
        for (const k of daten.karten) {
          if (k.datev_konto !== null || k.datev_gegenkonto !== null) await api.aendern(`/api/kreditkarten/${k.id}`, { datev_konto: null, datev_gegenkonto: null })
        }
        if (daten.stamm) await api.ersetzen(`/api/mandanten/${mandant.id}/datev-stamm`, kopf)
      } else if (wie === 'mandant') {
        if (kopf && kopfGeaendert) await api.ersetzen(`/api/mandanten/${mandant.id}/datev-stamm`, kopf)
        if (kontenGeaendert) {
          for (const k of daten.karten) await api.aendern(`/api/kreditkarten/${k.id}`, { datev_konto: kartenkonto, datev_gegenkonto: gegenkonto })
        }
      }
      await abfragen.invalidateQueries()
      setzeGespeichert(wie === 'vorgabe' ? 'Als Vorgabe für alle gespeichert.' : 'Gespeichert.')
      setTimeout(() => setzeGespeichert(null), 3000)
    } catch (problem) {
      setzeFehler(fehlerText(problem, 'Die Stammdaten konnten nicht gespeichert werden.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  const ersteVorgabe = !v
  const sperre = laeuft || !name.trim() || !kopf

  return (
    <form className="formular" onSubmit={(e) => { e.preventDefault(); if (NUR_CAMT) { if (mandantGeaendert && name.trim()) void speichern('nur-mandant') } else void speichern(ersteVorgabe ? 'vorgabe' : 'mandant') }}>
      <div className="abschnitt-kopf"><h2>{NUR_CAMT ? 'Stammdaten' : 'Stammdaten für den Buchungsstapel'}</h2></div>
      {fehler && <div className="fehler">{fehler}</div>}
      <div className="feldreihe">
        <div><label htmlFor="mb-name">Name</label><input id="mb-name" type="text" required autoComplete="off" data-1p-ignore value={name} onChange={(e) => setzeName(e.target.value)} /></div>
        <div><label htmlFor="mb-nummer">Mandantennummer in DATEV</label><input id="mb-nummer" type="text" inputMode="numeric" maxLength={5} className="zahl" autoComplete="off" data-1p-ignore value={nummer} onChange={(e) => setzeNummer(nurZiffern(e.target.value))} /></div>
      </div>
      {!NUR_CAMT && <>
      <div className="feldreihe">
        <div><label htmlFor="ds-berater">Beraternummer</label><input id="ds-berater" type="text" inputMode="numeric" maxLength={7} className="zahl" autoComplete="off" data-1p-ignore value={berater} onChange={(e) => setzeBerater(nurZiffern(e.target.value))} /></div>
        <div><label htmlFor="ds-laenge">Sachkontenlänge</label><input id="ds-laenge" type="text" inputMode="numeric" maxLength={1} className="zahl" autoComplete="off" data-1p-ignore value={laenge} onChange={(e) => setzeLaenge(nurZiffern(e.target.value))} /></div>
      </div>
      <div>
        <label>Beginn des Wirtschaftsjahres</label>
        <div className="wahl" role="group" aria-label="Beginn des Wirtschaftsjahres" style={{ flexWrap: 'wrap' }}>
          {MONATE.map((m, i) => (
            <button key={m} type="button" aria-pressed={monat === i + 1} onClick={() => setzeMonat(i + 1)}>{m}</button>
          ))}
        </div>
      </div>
      <div className="feldreihe">
        <div><label htmlFor="ds-konto">Kartenkonto</label><input id="ds-konto" type="text" inputMode="numeric" className="zahl" autoComplete="off" data-1p-ignore value={konto} onChange={(e) => setzeKonto(nurZiffern(e.target.value))} /></div>
        <div><label htmlFor="ds-gegen">Gegenkonto</label><input id="ds-gegen" type="text" inputMode="numeric" className="zahl" autoComplete="off" data-1p-ignore value={gegen} onChange={(e) => setzeGegen(nurZiffern(e.target.value))} /></div>
      </div>
      </>}

      {NUR_CAMT ? (
      <div className="formular-fuss">
        {gespeichert && <span className="leise">{gespeichert}</span>}
        <button type="button" className="knopf gefuellt" disabled={laeuft || !name.trim() || !mandantGeaendert} onClick={() => void speichern('nur-mandant')}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</button>
      </div>
      ) : (
      <div className="formular-fuss">
        <span className="links leiser">
          {ersteVorgabe
            ? 'Diese Angaben werden als Vorgabe für alle weiteren Abrechnungen Ihrer Kanzlei gespeichert. Einzelne Mandanten können Sie jederzeit abweichend speichern.'
            : vorgabeGeaendert
              ? 'Die Angaben weichen von der Vorgabe Ihrer Kanzlei ab. Nur für diesen Mandanten speichern oder als neue Vorgabe für alle?'
              : 'Aus der Vorgabe Ihrer Kanzlei. Ändern Sie etwas, können Sie es nur für diesen Mandanten oder für alle speichern.'}
        </span>
        {gespeichert && <span className="leise">{gespeichert}</span>}
        {ersteVorgabe ? (
          <button type="submit" className="knopf gefuellt" disabled={sperre}>{laeuft ? 'Wird gespeichert …' : 'Vorgabe speichern'}</button>
        ) : vorgabeGeaendert ? (
          <>
            <button type="button" className="knopf" disabled={sperre} onClick={() => void speichern('mandant')}>Nur für diesen Mandanten</button>
            <button type="button" className="knopf gefuellt" disabled={sperre} onClick={() => void speichern('vorgabe')}>Für alle speichern</button>
          </>
        ) : (
          <button type="submit" className="knopf gefuellt" disabled={sperre || !mandantGeaendert}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</button>
        )}
      </div>
      )}
    </form>
  )
}

/* ─────────────────────────────────────── Karte anlegen oder ändern */

function KarteFormular({ mandantId, karte, vorgabe, aufSchliessen }: { mandantId: string; karte: Kreditkarte | null; vorgabe: DatevVorgabe | null; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const neu = karte === null
  const [werte, setzeWerte] = useState(() => ({
    anbieter: karte?.anbieter ?? '',
    endziffern: karte?.endziffern ?? '',
    bezeichnung: karte?.bezeichnung ?? '',
    inhaber: karte?.inhaber ?? '',
    datev_konto: karte?.datev_konto != null ? String(karte.datev_konto) : '',
    datev_gegenkonto: karte?.datev_gegenkonto != null ? String(karte.datev_gegenkonto) : '',
  }))
  const [fehler, setzeFehler] = useState<{ text: string; feld?: string } | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const ziffernFeld = (name: keyof typeof werte) => name === 'endziffern' || name === 'datev_konto' || name === 'datev_gegenkonto'
  const feld = (name: keyof typeof werte) => ({
    value: werte[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const roh = ziffernFeld(name) ? nurZiffern(e.target.value) : e.target.value
      setzeWerte({ ...werte, [name]: name === 'endziffern' ? roh.slice(0, 4) : roh })
    },
    'aria-invalid': fehler?.feld === name,
    autoComplete: 'off',
    'data-1p-ignore': true,
  })

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    const konten = {
      bezeichnung: werte.bezeichnung.trim() || null,
      inhaber: werte.inhaber.trim() || null,
      datev_konto: zahlOderNull(werte.datev_konto),
      datev_gegenkonto: zahlOderNull(werte.datev_gegenkonto),
    }
    try {
      if (neu) {
        await api.senden(`/api/mandanten/${mandantId}/karten`, { anbieter: werte.anbieter.trim(), endziffern: werte.endziffern, ...konten })
      } else {
        // Anbieter und Endziffern nur, wenn sie sich geaendert haben: nach einem Export sperrt der Server sie.
        const kennung: Record<string, string> = {}
        if (werte.anbieter.trim() !== karte.anbieter) kennung['anbieter'] = werte.anbieter.trim()
        if (werte.endziffern !== karte.endziffern) kennung['endziffern'] = werte.endziffern
        await api.aendern(`/api/kreditkarten/${karte.id}`, { ...kennung, ...konten })
      }
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler({ text: fehlerText(problem, 'Die Karte konnte nicht gespeichert werden.'), ...(problem instanceof ApiFehler && problem.feld ? { feld: problem.feld } : {}) })
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel={neu ? 'Karte hinzufügen' : 'Karte bearbeiten'} offen aufSchliessen={aufSchliessen} breit>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler.text}</div>}
        <div className="feldreihe">
          <div><label htmlFor="kf-anbieter">Anbieter</label><input id="kf-anbieter" type="text" required placeholder="American Express" {...feld('anbieter')} /></div>
          <div className="schmal"><label htmlFor="kf-ende">Letzte vier Ziffern</label><input id="kf-ende" type="text" inputMode="numeric" required className="zahl" placeholder="1009" {...feld('endziffern')} /></div>
        </div>
        <div><label htmlFor="kf-bez">Bezeichnung</label><input id="kf-bez" type="text" placeholder="Firmenkarte Geschäftsführung" {...feld('bezeichnung')} /></div>
        <div>
          <label htmlFor="kf-inh">Karteninhaber</label>
          <input id="kf-inh" type="text" placeholder="Jonas Meier" {...feld('inhaber')} />
        </div>
        {!NUR_CAMT && <div className="feldreihe">
          <div>
            <label htmlFor="kf-konto">Kartenkonto</label>
            <input id="kf-konto" type="text" inputMode="numeric" className="zahl" placeholder={vorgabe?.kartenkonto != null ? String(vorgabe.kartenkonto) : ''} {...feld('datev_konto')} />
          </div>
          <div>
            <label htmlFor="kf-gegen">Gegenkonto</label>
            <input id="kf-gegen" type="text" inputMode="numeric" className="zahl" placeholder={vorgabe?.gegenkonto != null ? String(vorgabe.gegenkonto) : ''} {...feld('datev_gegenkonto')} />
          </div>
        </div>}
        {NUR_CAMT && karte?.ersatz_iban && (
          <div><label>Ersatz-IBAN für DATEV</label><div className="zahl">{iban(karte.ersatz_iban)}</div></div>
        )}
        {!neu && <div className="hinweis">Anbieter und Endziffern lassen sich nur ändern, solange für diese Karte noch kein Export erzeugt wurde.</div>}
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft || !werte.anbieter.trim() || werte.endziffern.length !== 4}>{laeuft ? 'Wird gespeichert …' : neu ? 'Hinzufügen' : 'Speichern'}</button>
        </div>
      </form>
    </Fenster>
  )
}

function KarteEntfernen({ karte, aufSchliessen }: { karte: Kreditkarte; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  async function entfernen() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/kreditkarten/${karte.id}`)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler(fehlerText(problem, 'Die Karte konnte nicht entfernt werden.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Karte entfernen" offen aufSchliessen={aufSchliessen}>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}
        <p className="leise"><span className="klein-sache">{karte.anbieter} {endeKurz(karte.endziffern)}</span> wird beim Mandanten entfernt.</p>
        <p className="leiser">Abrechnungen, die über diese Karte zugeordnet wurden, sind danach wieder ohne Mandant. Nach einem Export geht das nicht mehr.</p>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="button" className="knopf gefuellt loeschknopf" disabled={laeuft} onClick={() => void entfernen()}>{laeuft ? 'Wird entfernt …' : 'Entfernen'}</button>
        </div>
      </div>
    </Fenster>
  )
}
